import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const Carousel = ({ images = [], interval = 4500, testId = 'carousel' }) => {
  const [current, setCurrent] = useState(0);
  const total = images.length;

  const goTo = useCallback((index) => {
    setCurrent((index + total) % total);
  }, [total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, interval);
    return () => clearInterval(timer);
  }, [total, interval]);

  if (total === 0) return null;

  return (
    <div
      className="relative w-full overflow-hidden rounded-sm border-2 border-slate-200 bg-slate-900 shadow-lg group"
      data-testid={`${testId}-root`}
    >
      <div className="relative h-[300px] sm:h-[420px] lg:h-[520px]">
        {images.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            data-testid={`${testId}-slide-${index}`}
          >
            <img
              src={img.src}
              alt={img.caption || `Slide ${index + 1}`}
              className="w-full h-full object-contain"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            {img.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent px-6 py-5 z-20">
                <p className="text-white text-sm sm:text-base font-medium">{img.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {total > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-[#FF7F2A] hover:text-white text-slate-900 rounded-full p-2.5 shadow-md transition-all duration-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-[#FF7F2A] hover:text-white text-slate-900 rounded-full p-2.5 shadow-md transition-all duration-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === current ? 'w-6 bg-[#FF7F2A]' : 'w-2 bg-white/60 hover:bg-white'
                }`}
                data-testid={`${testId}-dot-${index}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Carousel;
